import React from "react";
import type { CSSProperties } from "react";
import Text from "antd/es/typography/Text";
import { formatPrice } from "@openings/booking-core";

export type ServiceItemRowProps = {
  /** Service name / title. */
  title: string;
  /** Numeric price — only shown when > 0. */
  price?: number;
  /** Append "+" after price (e.g. "$50+"). */
  up?: boolean;
  /** Pre-formatted duration label (e.g. "40 mins", "1h 30m"). */
  durationLabel?: string | null;
  /** Optional description shown below the header row. */
  description?: string;
  /** Style applied to the service name Text. */
  nameStyle?: CSSProperties;
  /** Style applied to the price Text. */
  priceStyle?: CSSProperties;
  /** Style applied to the duration Text. */
  durationStyle?: CSSProperties;
  /** Style applied to the description Text. */
  descriptionStyle?: CSSProperties;
};

/**
 * A single editorial service row with name + price followed by supporting
 * duration and description metadata.
 */
export function ServiceItemRow({
  title,
  price,
  up,
  durationLabel,
  description,
  nameStyle,
  priceStyle,
  durationStyle,
  descriptionStyle,
}: ServiceItemRowProps) {
  const hasPrice = typeof price === "number" && price > 0;
  const hasDuration = Boolean(durationLabel);

  return (
    <div
      className="openings-booking-service-row"
      data-service-item
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--openings-booking-spacing-xs, 4px)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "var(--openings-booking-spacing-lg, 16px)",
        }}
      >
        <Text
          style={{
            ...nameStyle,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Text>
        {hasPrice && (
          <Text
            style={{
              ...priceStyle,
              flexShrink: 0,
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatPrice(price)}
            {up ? "+" : ""}
          </Text>
        )}
      </div>
      {hasDuration ? (
        <Text style={durationStyle}>{durationLabel}</Text>
      ) : null}
      {description ? (
        <Text
          style={{
            ...descriptionStyle,
            overflowWrap: "anywhere",
          }}
        >
          {description}
        </Text>
      ) : null}
    </div>
  );
}
